import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const QUICK = ["❤️", "😂", "😮", "😢", "👏", "🔥"];

/** Instagram-style bottom bar: quick emoji reactions + DM reply, shown on other people's stories. */
export default function StoryReplyBar({ story, onFocusChange, onSent }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [flying, setFlying] = useState([]);
  const [myReaction, setMyReaction] = useState(story?.my_reaction || null);
  const inputRef = useRef(null);

  useEffect(() => { setText(""); setMyReaction(story?.my_reaction || null); }, [story?.story_id]); // eslint-disable-line

  const send = async (payload, emoji) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/chat/stories/${story.story_id}/reply`, payload);
      if (emoji) {
        setMyReaction(emoji);
        const id = Date.now();
        setFlying((f) => [...f, { id, emoji }]);
        setTimeout(() => setFlying((f) => f.filter((x) => x.id !== id)), 1300);
      } else {
        setText("");
        toast.success("Resposta enviada por mensagem");
      }
      onSent?.(r.data, emoji);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível enviar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="absolute bottom-0 left-0 right-0 z-40 p-3 pt-10 bg-gradient-to-t from-black/80 via-black/40 to-transparent" data-testid="story-reply-bar"
      onClick={(e) => e.stopPropagation()}>
      <div className="relative h-8 pointer-events-none">
        {flying.map((f) => (
          <span key={f.id} className="absolute left-1/2 -translate-x-1/2 bottom-0 text-4xl animate-story-fly" data-testid="story-reaction-fly">{f.emoji}</span>
        ))}
      </div>
      <div className="flex justify-between px-1 mb-2" data-testid="story-quick-reactions">
        {QUICK.map((e) => (
          <button key={e} type="button" data-testid={`story-react-${e}`} disabled={busy}
            onClick={() => send({ emoji: e }, e)}
            className={`text-2xl leading-none transition-transform hover:scale-125 active:scale-95 ${myReaction === e ? "drop-shadow-[0_0_8px_rgba(255,255,255,0.9)] scale-110" : ""}`}
            aria-label={`Reagir ${e}`}>{e}</button>
        ))}
      </div>
      <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); if (text.trim()) send({ text: text.trim() }); }}>
        <input
          ref={inputRef}
          data-testid="story-reply-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onFocus={() => onFocusChange?.(true)}
          onBlur={() => onFocusChange?.(false)}
          placeholder={`Responder a ${story?.author_name?.split(" ")[0] || "story"}...`}
          className="flex-1 h-10 rounded-full bg-white/10 border border-white/40 text-white placeholder:text-white/60 px-4 text-sm outline-none focus:border-white backdrop-blur-sm"
          maxLength={1000}
        />
        <button type="submit" data-testid="story-reply-send" disabled={busy || !text.trim()} aria-label="Enviar resposta"
          className="w-10 h-10 rounded-full bg-white text-slate-900 flex items-center justify-center disabled:opacity-40">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>
    </div>
  );
}
